import React from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../../../theme/Colors';

export type AvatarSize = 'small' | 'medium' | 'large' | 'xlarge';
export type AvatarShape = 'circle' | 'square';

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: AvatarSize;
  shape?: AvatarShape;
  status?: 'online' | 'offline' | 'away' | 'busy';
  onClick?: () => void;
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'medium',
  shape = 'circle',
  status,
  onClick,
  className,
}) => {
  const initials = name
    ? name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  return (
    <AvatarContainer
      $size={size}
      $shape={shape}
      $clickable={!!onClick}
      onClick={onClick}
      className={className}
    >
      {src ? (
        <AvatarImage src={src} alt={alt || name} />
      ) : (
        <AvatarFallback $size={size}>{initials}</AvatarFallback>
      )}
      {status && <StatusIndicator $status={status} $size={size} />}
    </AvatarContainer>
  );
};

// Size configurations
const sizeConfig = {
  small: {
    size: '32px',
    fontSize: '14px',
    statusSize: '8px',
    statusBorder: '2px',
  },
  medium: {
    size: '40px',
    fontSize: '16px',
    statusSize: '10px',
    statusBorder: '2px',
  },
  large: {
    size: '56px',
    fontSize: '20px',
    statusSize: '12px',
    statusBorder: '3px',
  },
  xlarge: {
    size: '80px',
    fontSize: '28px',
    statusSize: '16px',
    statusBorder: '3px',
  },
};

const statusColors = {
  online: '#10B981',
  offline: '#6B7280',
  away: '#F59E0B',
  busy: '#EF4444',
};

const AvatarContainer = styled.div<{
  $size: AvatarSize;
  $shape: AvatarShape;
  $clickable: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => sizeConfig[$size].size};
  height: ${({ $size }) => sizeConfig[$size].size};
  border-radius: ${({ $shape }) => $shape === 'circle' ? '50%' : '8px'};
  overflow: hidden;
  background-color: #E5E7EB;
  transition: transform 0.2s ease-in-out;
  
  ${({ $clickable }) => $clickable && css`
    cursor: pointer;
    
    &:hover {
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
  `}
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div<{ $size: AvatarSize }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${COLORS.primary};
  color: white;
  font-size: ${({ $size }) => sizeConfig[$size].fontSize};
  font-weight: 600;
  user-select: none;
`;

const StatusIndicator = styled.div<{
  $status: 'online' | 'offline' | 'away' | 'busy';
  $size: AvatarSize;
}>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${({ $size }) => sizeConfig[$size].statusSize};
  height: ${({ $size }) => sizeConfig[$size].statusSize};
  border-radius: 50%;
  background-color: ${({ $status }) => statusColors[$status]};
  border: ${({ $size }) => sizeConfig[$size].statusBorder} solid white;
`;

export default Avatar;